import { Grid } from '@design-systems/canvas-ui'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useOutletContext } from 'react-router-dom'
import { isGroup, isStore } from 'utils/location'
import {
    buildStoresHistoricalData,
    buildTableDataForFulfillment,
    buildTableDataForStoresFormat,
    formattedTimeStamp,
    parseFormattedNumber,
} from '../../helpers'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { useEffect, useMemo, useState } from 'react'
import { useFulFillmentPickupHistorical } from 'hooks/store-fulfillment/useStoreFulfillment'
import useCountdown from 'hooks/utils/useCountdown'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import { TimePeriods } from 'components/timeperiods'
import '../../store-fulfillment.css'
import { filterRows } from 'utils/data'

export const StoreFulfillmentPickup = () => {
    const { t, currentLocation, env, session, logout } = useEssentials()
    const { storeFulfillment } = TRANSLATION_KEYS
    const isStoreLocation = isStore(currentLocation)
    const [timePeriod, setTimePeriod] = useState<string>('today')
    const [translatedTimePeriod, setTranslatedTimePeriod] = useState(
        t(storeFulfillment.timePeriods.today),
    )
    const [viewIndex, setViewIndex] = useState<number>(0)
    const availableTimeDimensions = useMemo(
        () =>
            isStoreLocation
                ? [
                      'today',
                      'prior_day',
                      'week_to_day',
                      'prior_week',
                      'month_to_day',
                      'year_to_day',
                  ]
                : ['today'],
        [isStoreLocation],
    )

    const translatedTimePeriods = useMemo(
        () => [
            t(storeFulfillment.timePeriods.today),
            t(storeFulfillment.timePeriods.yesterday),
            t(storeFulfillment.timePeriods.weekToDate),
            t(storeFulfillment.timePeriods.lastWeek),
            t(storeFulfillment.timePeriods.monthToDate),
            t(storeFulfillment.timePeriods.yearToDate),
        ],
        [t, storeFulfillment.timePeriods],
    )

    const fulfillmentHeaders = useMemo(
        () => [
            t(storeFulfillment.headings.location),
            t(storeFulfillment.headings.infPercent),
            t(storeFulfillment.headings.pickedOnTime),
            t(storeFulfillment.headings.driveUpWaitTime),
        ],
        [t, storeFulfillment.headings],
    )
    const unitVolumnHeaders = useMemo(
        () => [
            t(storeFulfillment.headings.location),
            t(storeFulfillment.headings.openLateUnits),
            t(storeFulfillment.headings.openUnits),
            t(storeFulfillment.headings.pickedUnitsToday),
            t(storeFulfillment.headings.forecastedToday),
        ],
        [t, storeFulfillment.headings],
    )
    const teamMemberHeaders = useMemo(
        () => [
            t(storeFulfillment.headings.teamMember),
            t(storeFulfillment.headings.totalPickupInf),
            t(storeFulfillment.headings.totalPickupAttempted),
            t(storeFulfillment.headings.pickupUnitsAttempted),
            t(storeFulfillment.headings.groceryUnitsAttempted),
            t(storeFulfillment.headings.totalPickupPickProductivity),
        ],
        [t, storeFulfillment.headings],
    )
    const historicalFulfillmentHeaders = useMemo(() => {
        const modifiedHeaders = [...fulfillmentHeaders.slice(1)]
        modifiedHeaders.splice(1, 0, t(storeFulfillment.headings.infDollars))
        return modifiedHeaders
    }, [fulfillmentHeaders, t, storeFulfillment.headings])

    const historicalUnitVolumnHeaders = useMemo(
        () => [
            t(storeFulfillment.headings.receivedUnits),
            t(storeFulfillment.headings.pickedUnits),
            t(storeFulfillment.headings.forecastedUnits),
        ],
        [t, storeFulfillment.headings],
    )

    const { data } = useOutletContext<{ data: any }>()
    const [tableData, setTableData] = useState<any[]>([])
    const [originalTableData, setOriginalTableData] = useState<any[]>([])
    const {
        storesHistoricalData,
        isLoading,
        isError,
        error: historicalError,
        refetch: refetchHistorical,
    } = useFulFillmentPickupHistorical({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        filters: {
            time_dimension: timePeriod,
        },
        enabled: timePeriod !== 'today',
    })
    const isHistoricalView = isStoreLocation && timePeriod !== 'today'
    useEffect(() => {
        if (!data) return

        const timeStamp = isHistoricalView
            ? ''
            : isStoreLocation
              ? data?.[0]?.order_pickup_unit?.timestamp
              : data?.[0]?.order_pickup_unit?.order_pickup_landscape
                    ?.opu_time_utc

        const fulfillmentTitle = isHistoricalView
            ? t(translatedTimePeriod)
            : `${t(translatedTimePeriod)} ${t(storeFulfillment.ndd.asOf)} ${formattedTimeStamp(timeStamp)}`

        const unitVolumnTitle = t(storeFulfillment.titles.unitVolume)
        const teamMemberTitle = t(
            storeFulfillment.titles.teamMemberProductivity,
        )

        const combinedTables = isHistoricalView
            ? buildStoresHistoricalData(
                  storesHistoricalData,
                  historicalFulfillmentHeaders,
                  historicalUnitVolumnHeaders,
              ).combinedTables
            : isStoreLocation
              ? buildTableDataForStoresFormat(
                    data?.[0],
                    fulfillmentHeaders,
                    unitVolumnHeaders,
                    teamMemberHeaders,
                ).combinedTables
              : buildTableDataForFulfillment(
                    data?.[0]?.order_pickup_unit?.order_pickup_landscape,
                    fulfillmentHeaders,
                    unitVolumnHeaders,
                    currentLocation,
                ).combinedTables

        const initialTableData = [
            {
                title: fulfillmentTitle,
                table: (combinedTables ?? [])[0],
            },
            {
                title: unitVolumnTitle,
                table: (combinedTables ?? [])[1],
            },
            ...(isStoreLocation && !isHistoricalView
                ? [
                      {
                          title: teamMemberTitle,
                          table: (combinedTables ?? [])[2],
                      },
                  ]
                : []),
        ]
        setTableData(initialTableData)
        setOriginalTableData(initialTableData) // Store original order
    }, [
        data,
        currentLocation,
        t,
        storeFulfillment,
        timePeriod,
        fulfillmentHeaders,
        unitVolumnHeaders,
        teamMemberHeaders,
        isStoreLocation,
        storesHistoricalData,
        historicalFulfillmentHeaders,
        historicalUnitVolumnHeaders,
        isHistoricalView,
        translatedTimePeriod,
    ])

    const sortDataRow = (
        sortDirection: 'asc' | 'desc' | undefined,
        field: string,
        tableTitle: string,
    ) => {
        if (!field) {
            return
        }
        if (!sortDirection) {
            const originalTable = originalTableData.find(
                (tableObj) => tableObj.title === tableTitle,
            )

            if (originalTable) {
                const updatedTableData = tableData.map((tableObj) =>
                    tableObj.title === tableTitle
                        ? { ...tableObj, table: originalTable.table }
                        : tableObj,
                )
                setTableData(updatedTableData)
            }
            return
        }

        const normalizeString = (str: string) =>
            str
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9]/g, '')

        const normalizedField = normalizeString(field)

        const tableToSort = tableData.find(
            (tableObj) => tableObj.title === tableTitle,
        )

        if (!tableToSort) return

        const columnIndex =
            tableToSort.table.headings?.findIndex(
                (heading: { value: string }) =>
                    normalizeString(heading.value) === normalizedField,
            ) ?? -1

        if (columnIndex === -1) return

        const allRows = tableToSort.table.rows

        const currentLocationIndex = allRows.findIndex(
            (row: { [key: number]: { value: string } }) =>
                row[0]?.value === currentLocation,
        )

        let pinnedRows: typeof allRows = []
        let sortableRows: typeof allRows = []

        if (currentLocationIndex !== -1) {
            pinnedRows = allRows.slice(0, currentLocationIndex + 1)
            sortableRows = allRows.slice(currentLocationIndex + 1)
        } else {
            sortableRows = [...allRows]
        }

        const sortedRows = [...sortableRows].sort((a, b) => {
            const valueA = a[columnIndex]?.value || ''
            const valueB = b[columnIndex]?.value || ''

            const numericValueA = parseFormattedNumber(String(valueA))
            const numericValueB = parseFormattedNumber(String(valueB))

            return sortDirection === 'asc'
                ? numericValueA - numericValueB
                : numericValueB - numericValueA
        })

        const updatedTable = {
            ...tableToSort.table,
            rows: [...pinnedRows, ...sortedRows],
        }

        const updatedTableData = tableData.map((tableObj) =>
            tableObj.title === tableTitle
                ? { ...tableObj, table: updatedTable }
                : tableObj,
        )

        setTableData(updatedTableData)
    }

    const handleTimeDimensionChange = (timePeriod: string, index: number) => {
        setViewIndex(index)
    }
    const count = useCountdown(5, logout, historicalError)

    useEffect(() => {
        const newTimePeriod = availableTimeDimensions[viewIndex]
        setTimePeriod(newTimePeriod)

        const getTimePeriodValue = (index: number) => {
            const keys = Object.keys(storeFulfillment.timePeriods)
            return storeFulfillment.timePeriods[
                keys[index] as keyof typeof storeFulfillment.timePeriods
            ]
        }
        setTranslatedTimePeriod(getTimePeriodValue(viewIndex))
    }, [
        viewIndex,
        availableTimeDimensions,
        storeFulfillment.timePeriods,
        storeFulfillment,
    ])

    return (
        <div>
            {isStoreLocation && (
                <TimePeriods
                    timePeriods={translatedTimePeriods}
                    activeTimePeriod={translatedTimePeriods[viewIndex]}
                    onTabSelect={handleTimeDimensionChange}
                    page="store fulfillment pickup"
                    isTimePeriodSticky={false}
                />
            )}
            <Grid.Container>
                <Grid.Item
                    className="hc8-pa-none"
                    size={{
                        xs: 12,
                    }}
                >
                    {isHistoricalView && isLoading ? (
                        <div
                            className="placeholder-container"
                            data-testid="placeholder"
                        >
                            <BasicPlaceholder
                                type="table"
                                rows={5}
                                columns={3}
                            />
                        </div>
                    ) : isHistoricalView && (isError || !data) ? (
                        <ErrorAlert
                            error={historicalError}
                            count={count}
                            onRetry={() => refetchHistorical?.()}
                        />
                    ) : (
                        tableData?.map(({ title, table }, index) => {
                            const isThirdTable = index === 2
                            const tableWidths = isThirdTable
                                ? [125, 100, 100, 100, 100, 125]
                                : [90, 75, 90, 80, 90]
                            const isSortable = table?.rows?.length > 1
                            const payload = isGroup(currentLocation)
                                ? {
                                      [t(
                                          storeFulfillment.toggles
                                              .showDistricts,
                                      )]: [
                                          {
                                              title: '',
                                              subtitle: '',
                                              data: {
                                                  tab: filterRows(
                                                      table,
                                                      (row) =>
                                                          !row[0]?.value?.includes(
                                                              'T',
                                                          ),
                                                  ),
                                              },
                                          },
                                      ],
                                      [t(storeFulfillment.toggles.showStores)]:
                                          [
                                              {
                                                  title: '',
                                                  subtitle: '',
                                                  data: {
                                                      tab: filterRows(
                                                          table,
                                                          (row) =>
                                                              !row[0]?.value?.includes(
                                                                  'D',
                                                              ),
                                                      ),
                                                  },
                                              },
                                          ],
                                  }
                                : {
                                      'view in %': [
                                          {
                                              title: '',
                                              subtitle: '',
                                              data: { table },
                                          },
                                      ],
                                  }

                            return (
                                <GeneralizedComponent
                                    sortable={isSortable}
                                    key={title}
                                    payload={payload}
                                    title={title}
                                    scrollableWidth={true}
                                    widths={tableWidths}
                                    columnSize={
                                        !isGroup(currentLocation) ? 12 : 8
                                    }
                                    onSort={(sortDirection, field) =>
                                        sortDataRow(sortDirection, field, title)
                                    }
                                    columnSortSettingsByIndex={
                                        isThirdTable
                                            ? {
                                                  0: { sortable: false },
                                                  1: { sortable: true },
                                                  2: { sortable: true },
                                                  3: { sortable: true },
                                                  4: { sortable: true },
                                                  5: { sortable: true },
                                              }
                                            : {
                                                  1: { sortable: true },
                                              }
                                    }
                                />
                            )
                        })
                    )}
                </Grid.Item>
            </Grid.Container>
        </div>
    )
}
